import MenuTabs from "~/modules/order/components/MenuTabs/MenuTabs";
import MenuWrapper from "~/modules/order/components/MenuWrapper/MenuWrapper";
import DineInLanding from "~/modules/order/components/DineInLanding/DineInLanding";
import ProductsList from "~/modules/order/components/ProductsList/ProductsList";
import { useNavigate, useParams, useSearchParams } from "@remix-run/react";
import { useContext, useEffect, useRef, useState } from "react";
import MenuContext from "~/contexts/MenuContext";
import ProductModal from "~/modules/order/containers/ProductModal/ProductModal";
import invariant from "tiny-invariant";
import OrderContext from "~/contexts/OrderContext";
import { urlGen } from "~/helpers/routesHelpers";
import { CartLine, OrderSource } from "~/entities/orderEntities";
import { generateLine } from "~/helpers/cartHelpers";
import BannersAndInfoBar from "~/modules/order/components/BannersAndInfoBar/BannersAndInfoBar";
import { TabsPosition } from "~/entities/templateEntities";
import ThemeContext from "~/contexts/ThemeContext";
import ModalContext from "~/contexts/ModalContext";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { BannerAction, BannerActionType } from "~/entities/bannerEntities";
import FlashPromotionCard from "~/modules/order/components/FlashPromotion/FlashPromotion";

export default function Page() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const menuWrapperRef = useRef<HTMLDivElement>(null);

  const { orderSource, openStoreFinder, addOrderLine, redeemPromotionCode } =
    useContext(OrderContext);
  const { setShowAssetModal } = useContext(ModalContext);

  const params = useParams();
  invariant(params.siteId, "expected params.siteId");
  invariant(params.orderSource, "expected params.orderSource");

  const { menu, menus } = useContext(MenuContext);
  const { template } = useContext(ThemeContext);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [newLine, setNewLine] = useState<CartLine | null>(null);

  const [selectedTabCode, setSelectedTabCode] = useState<string | null>(null);

  const menuTabsRef = useRef<HTMLDivElement>(null);
  const productsListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedTabCode) {
      const tab = menu?.tabs.find((tab) => tab.items.length);
      if (tab) {
        setSelectedTabCode(tab?.code);
      }
    }
  }, [menu?.tabs, selectedTabCode]);

  const findMenuItem = (productCode: string) =>
    menu?.tabs
      .flatMap((tab) => tab.items)
      .find((x) => x.productCode === productCode);

  const onBannerAction = (action: BannerAction) => {
    switch (action.type) {
      case BannerActionType.OpenProduct: {
        // Opens the product so the customer can customise it before adding
        const item = action.productCode && findMenuItem(action.productCode);
        if (item && menu) {
          setNewLine(generateLine(item, menu.code));
        }
        break;
      }
      case BannerActionType.AddProductToOrder: {
        const item = action.productCode && findMenuItem(action.productCode);
        if (item && menu) {
          addOrderLine(generateLine(item, menu.code));
        }
        break;
      }
      case BannerActionType.AddVoucher: {
        if (action.voucherCode) {
          redeemPromotionCode(action.voucherCode);
        }
        break;
      }
      case BannerActionType.GoToLoyalty: {
        setShowAssetModal(true);
        break;
      }
      case BannerActionType.OpenLink: {
        if (action.url) {
          window.open(action.url, "_blank", "noopener,noreferrer");
        }
        break;
      }
      case BannerActionType.None:
        break;
    }
  };

  /**
   * Scroll to section
   */
  const isScrollingRef = useRef(false);
  const lastScrollPositionRef = useRef<number | null>(null);
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollToSection = (code: string) => {
    if (typeof document !== "undefined") {
      const section = document.getElementById(code);
      const menuHeight =
        !isMobile || template.menuScreen.tabs.position === TabsPosition.Top
          ? menuTabsRef.current?.offsetHeight ?? 0
          : 0;

      if (section && menuTabsRef.current) {
        isScrollingRef.current = true;

        const topPosition = section.offsetTop - menuHeight + 1;

        window.scrollTo({
          top: topPosition,
          behavior: "smooth",
        });
        setSelectedTabCode(code);

        if (scrollEndTimeoutRef.current) {
          clearTimeout(scrollEndTimeoutRef.current);
        }

        const checkScrollEnd = () => {
          const currentScrollPosition = window?.scrollY ?? 0;

          if (lastScrollPositionRef.current === currentScrollPosition) {
            isScrollingRef.current = false;
            clearTimeout(scrollEndTimeoutRef.current!);
            scrollEndTimeoutRef.current = null;
          } else {
            lastScrollPositionRef.current = currentScrollPosition;
            scrollEndTimeoutRef.current = setTimeout(checkScrollEnd, 100);
          }
        };

        lastScrollPositionRef.current = window?.scrollY;
        scrollEndTimeoutRef.current = setTimeout(checkScrollEnd, 100);
      }
    }
  };

  /**
   * Scroll
   */
  useEffect(() => {
    if (productsListRef.current) {
      const handleScroll = () => {
        if (isScrollingRef.current || typeof document === "undefined") return;

        const elements = Array.from(productsListRef.current?.children ?? []);

        const viewportHeight = window.innerHeight;

        const visibleParts = elements
          .filter((el) => el.id)
          .map((el) => {
            const elRect = el.getBoundingClientRect();

            const visibleTop = Math.max(elRect.top, 0);
            const visibleBottom = Math.min(elRect.bottom, viewportHeight);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            const elementHeight = elRect.height;

            const visibilityPercentage = (visibleHeight / elementHeight) * 100;

            return {
              code: el.id,
              percent: +visibilityPercentage.toFixed(2),
            };
          });

        setSelectedTabCode(
          visibleParts.sort((a, b) => b.percent - a.percent)[0]?.code ?? null,
        );
      };

      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const goToCheckout = () =>
    navigate(
      urlGen("Checkout", {
        menuCode: searchParams.get("menuCode"),
        tableNumber: params.table,
        siteId: params.siteId,
        orderSource: orderSource,
      }),
    );

  // Dine-in (QR) browses menu-first: a "Browse menu" grid of the store's MENUS,
  // then the chosen menu's tabs + scrolling list via the `menuCode` param (a
  // real route, no cart reset). A single-menu store skips the grid. Online /
  // catering keep the single scrolling list. See the QR/dine-in design §4.1.
  const isDineIn = orderSource === OrderSource.TABLE_ORDERING;
  const multiMenu = (menus?.length ?? 0) > 1;
  const showLanding = isDineIn && multiMenu && !searchParams.get("menuCode");

  if (!menu) {
    return (
      <div
        role="status"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 12,
          padding: "64px 24px",
          minHeight: "40vh",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 20 }}>No menu available</h2>
        <p style={{ margin: 0, maxWidth: 360, opacity: 0.75 }}>
          There&rsquo;s no active menu for this option at this store right now.
          Try another store, or switch ordering mode using the bar above.
        </p>
        <button
          type="button"
          onClick={() => openStoreFinder()}
          style={{
            marginTop: 4,
            padding: "10px 18px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            fontWeight: 600,
            background: template.global.button.primaryButtonColour,
            color: template.global.button.primaryButtonFontColour,
          }}
        >
          Choose another store
        </button>
      </div>
    );
  }

  const productModal = (
    <ProductModal
      isInsert={true}
      initLine={newLine}
      onClose={() => setNewLine(null)}
    />
  );

  // Dine-in landing: banner + "Browse menu" grid of the store's menus.
  if (showLanding) {
    return (
      <>
        {productModal}
        <FlashPromotionCard />
        <DineInLanding
          onBannerAction={onBannerAction}
          onViewOrder={goToCheckout}
        />
      </>
    );
  }

  // Dine-in menu view: the chosen menu's tabs + scrolling product list. The
  // back-to-grid + menu-switcher live in the top bar (StoreHeader) for a
  // multi-menu store. A single-menu store has no grid, so it shows the banner
  // here instead.
  if (isDineIn) {
    return (
      <>
        {productModal}
        <FlashPromotionCard />
        {!multiMenu && <BannersAndInfoBar onBannerAction={onBannerAction} />}
        <MenuTabs
          menuTabsRef={menuTabsRef}
          tabs={menu.tabs ?? []}
          selectedTabCode={selectedTabCode}
          scrollToSection={scrollToSection}
        />
        <MenuWrapper ref={menuWrapperRef}>
          <ProductsList
            productsListRef={productsListRef}
            menu={menu}
            onSelectItem={(item) => setNewLine(generateLine(item, menu.code))}
            onMobileCheckout={goToCheckout}
          />
        </MenuWrapper>
      </>
    );
  }

  // Online / catering: single scrolling list with category chips.
  return (
    <>
      {productModal}
      {/* Above the banner strip, never inside it: this is a timed card that
          arrives and leaves, not part of the page furniture */}
      <FlashPromotionCard />
      <BannersAndInfoBar onBannerAction={onBannerAction} />
      <MenuTabs
        menuTabsRef={menuTabsRef}
        tabs={menu.tabs ?? []}
        selectedTabCode={selectedTabCode}
        scrollToSection={scrollToSection}
      />
      <MenuWrapper ref={menuWrapperRef}>
        <ProductsList
          productsListRef={productsListRef}
          menu={menu}
          onSelectItem={(item) => setNewLine(generateLine(item, menu.code))}
          onMobileCheckout={goToCheckout}
        />
      </MenuWrapper>
    </>
  );
}
